import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { trackPageView } from '@/lib/analytics';

const PAGE_TITLES = {
  '/about': 'About Us',
  '/blog': 'Blog',
  '/privacy': 'Privacy Policy',
  '/terms': 'Terms of Service',
  '/cookies': 'Cookie Policy',
  '/portal': 'Portal — Select Role',
  '/elderly': 'Elderly Portal — Code Entry',
  '/onboarding': 'Onboarding — Add Family Members',
  '/dashboard': 'Dashboard — Home',
  '/medications': 'Dashboard — Medications',
  '/reports': 'Dashboard — Health Reports',
  '/health-trends': 'Dashboard — Health Trends',
  '/appointments': 'Dashboard — Appointments',
  '/carers': 'Dashboard — Carers',
  '/consultations': 'Dashboard — Consultations',
  '/properties': 'Dashboard — Properties',
  '/insurance': 'Dashboard — Insurance',
  '/chat': 'Dashboard — AI Chat',
  '/plans': 'Dashboard — Plans',
  '/payment-success': 'Payment — Success',
  '/profile': 'Dashboard — Profile',
  '/admin': 'Admin — Dashboard',
  '/admin/heatmap': 'Admin — Heatmap',
  '/carer-admin': 'Carer — Dashboard',
  '/cs': 'CS — Dashboard',
  '/pm': 'PM — Dashboard',
  '/referrals': 'Dashboard — Referrals',
  '/data-control': 'Dashboard — Data Control',
  '/feedback': 'Survey — Help Us Build Right',
};

const AUTH_ROLE_MAP = {
  user_signup: 'Auth — Family Sign Up',
  user_login: 'Auth — Family Log In',
  carer: 'Auth — Carer Log In',
  admin: 'Auth — Admin Log In',
  customer_service: 'Auth — CS Log In',
  property_manager: 'Auth — PM Log In',
};

export function usePageTracking() {
  const location = useLocation();
  const { user } = useAuth();

  useEffect(() => {
    let path = location.pathname;
    let title = PAGE_TITLES[path];

    // "/" is either "Home" (public) or "Dashboard — Home" (authenticated)
    if (path === '/') {
      title = user ? 'Dashboard — Home' : 'Home';
    }

    // Dynamic: /scheduling/:carerId
    if (!title && path.startsWith('/scheduling/')) {
      title = 'Dashboard — Carer Scheduling';
    }

    // Auth page: derive from query params
    if (path === '/auth') {
      const params = new URLSearchParams(location.search);
      const role = params.get('role') || 'user';
      const mode = params.get('mode') || 'login';
      if (role === 'user') {
        title = mode === 'signup' ? AUTH_ROLE_MAP.user_signup : AUTH_ROLE_MAP.user_login;
      } else {
        title = AUTH_ROLE_MAP[role] || 'Auth — Log In';
      }
    }

    if (!title) title = '404 — Not Found';

    // The address is not passed on: lib/analytics reports it itself, without
    // record ids or the query string.
    trackPageView(title);
  }, [location, user]);
}
